import { getUserAvatarColor } from '@/utils/collaborationUserColor';
import { getUserInitials } from '@/utils/general';
import { Avatar, FormHelperText, Grid, MenuItem, Select, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './InviteForm.styles';

export interface InviteRoleOption {
  // `value` is a string in most flows but a numeric role id in the audit-client flow.
  value: string | number;
  label: ReactNode;
  disabled?: boolean;
  disclaimer?: string;
  disclaimerColor?: string;
}

export interface InviteCandidate {
  id?: number;
  email: string;
  firstName?: string;
  lastName?: string;
  logo?: string;
  teamMemberId?: string | number | null;
}

export interface InviteValue {
  email: string;
  role?: string | number;
  [key: string]: unknown;
}

export type InviteFormError = string | { message?: string; userId?: number } | null | undefined;

export interface InviteFormProps {
  disabled?: boolean;
  allowMenu?: boolean;
  handleClickUser?: (email: string, user: InviteCandidate) => void;
  filteredUsers?: InviteCandidate[];
  invite: InviteValue;
  inviteRoles: InviteRoleOption[];
  isInviting?: boolean;
  inputData: (key: string, value: string) => void;
  inviteMember: () => void;
  onChangeInvite: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * i18n key for the note shown under the field when there is no error — supplied by the
   * caller's active role option. Callers whose roles carry no disclaimer omit it.
   */
  disclaimer?: string;
  /** Optional colour override for `disclaimer` (roles use it to flag elevated access). */
  disclaimerColor?: string;
  placeholder?: string;
  error?: InviteFormError;
  /**
   * Called when the user edits the field or picks a candidate. This component only ever
   * CLEARS the error, so it hands back no value: each caller's error state has a different
   * shape (a string here, a `{ userId, message }` API payload there) and it is theirs to reset.
   */
  onClearError?: () => void;
}

const InviteForm = ({
  disabled,
  allowMenu = true,
  handleClickUser,
  filteredUsers,
  invite,
  inviteRoles,
  isInviting = false,
  inputData,
  inviteMember,
  onChangeInvite,
  disclaimer,
  disclaimerColor,
  placeholder,
  error,
  onClearError,
}: InviteFormProps) => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'settings' });

  const [showMenu, setShowMenu] = useState(false);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    inviteMember();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onClearError?.();
    // TextField surfaces a union event; the field is a text input at runtime.
    onChangeInvite(e as ChangeEvent<HTMLInputElement>);
  };

  const handleSelectUser = (email: string, user: InviteCandidate) => {
    onClearError?.();
    handleClickUser?.(email, user);
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size='grow'>
            <TextField
              fullWidth
              disabled={disabled}
              placeholder={placeholder || t('invite_user')}
              value={invite.email}
              error={!!error}
              slotProps={{
                input: {
                  autoComplete: 'off',
                },
              }}
              onChange={handleChange}
              onFocus={() => setShowMenu(true)}
              onBlur={() => {
                setShowMenu(false);
              }}
            />
            {allowMenu && showMenu && !!filteredUsers?.length && (
              <S.RelativeSearch>
                <S.SearchWrapper>
                  {filteredUsers.map((item, index) => (
                    <S.InviteUserRow
                      key={index}
                      onMouseDown={() => handleSelectUser(item.email, item)}
                    >
                      <Avatar src={item.logo} color={getUserAvatarColor(item)}>
                        {getUserInitials(item)}
                      </Avatar>
                      <S.TitleName>
                        {item.firstName ? item.firstName + ' ' + item.lastName : item.email}
                      </S.TitleName>
                    </S.InviteUserRow>
                  ))}
                </S.SearchWrapper>
              </S.RelativeSearch>
            )}
          </Grid>

          <Grid size={3}>
            <Select
              fullWidth
              name='role'
              disabled={disabled}
              value={invite.role}
              onChange={(e) => inputData('role', e.target.value as string)}
            >
              {inviteRoles.map((role) => (
                <MenuItem key={role.value} value={role.value} disabled={role.disabled}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={2}>
            <S.InviteButton
              fullWidth
              variant='contained'
              type='submit'
              color='primary'
              loading={isInviting}
              disabled={!invite.email || disabled}
            >
              {t('invite')}
            </S.InviteButton>
          </Grid>
        </Grid>
      </form>
      {(error || disclaimer) && (
        <FormHelperText
          error={!!error}
          sx={{
            color: disclaimerColor,
          }}
        >
          {error ? (typeof error === 'string' ? error : error?.message) : t(disclaimer ?? '')}
        </FormHelperText>
      )}
    </div>
  );
};

export default InviteForm;
